import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { useAuthStore } from '@/features/auth'

const API_BASE_URL = import.meta.env.DEV ? 'http://localhost:8000/api' : '/api'

/**
 * Extracts a human-readable error message from any thrown value.
 * Use this when displaying API errors in the UI or toasts.
 */
export const extractApiError = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data
        if (typeof data === 'object' && data !== null) {
            const record = data as Record<string, unknown>
            if (typeof record.message === 'string' && record.message.length > 0) {
                return record.message
            }
            // FastAPI reports errors under `detail`: a string for HTTPException
            // (e.g. our 400 "A foam named 'X' already exists"), or a list of
            // {loc, msg, ...} objects for 422 request-validation errors.
            const { detail } = record
            if (typeof detail === 'string' && detail.length > 0) return detail
            if (Array.isArray(detail)) {
                const messages = detail
                    .map((item) =>
                        typeof item === 'object' && item !== null && 'msg' in item
                            ? String((item as { msg: unknown }).msg)
                            : '',
                    )
                    .filter(Boolean)
                if (messages.length > 0) return messages.join('; ')
            }
        }
        if (typeof data === 'string' && data.length > 0) return data
        if (error.code === 'ECONNABORTED') return 'Request timed out'
        if (!error.response) return 'Network error — check your connection'
        return error.message
    }
    if (error instanceof Error) return error.message
    return 'An unexpected error occurred'
}

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: { 'Content-Type': 'application/json' },
})

// Inject Bearer token from auth store on every request
api.interceptors.request.use(
    (config) => {
        const token = useAuthStore.getState().getToken()
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        // For multipart uploads, drop the JSON default so axios keeps the
        // FormData intact and the browser sets the multipart boundary.
        // (With the JSON content-type, axios serialises FormData to JSON and
        // the file is lost — see api.test.ts.)
        if (config.data instanceof FormData) {
            config.headers.delete('Content-Type')
        }
        return config
    },
    (error) => Promise.reject(error),
)

// On 401, trigger logout; normalize all errors to Error instances
api.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
            useAuthStore.getState().logout()
        }
        return Promise.reject(new Error(extractApiError(error)))
    },
)

export const apiService = {
    get:    <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.get<T>(url, config),
    post:   <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.post<T>(url, data, config),
    put:    <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.put<T>(url, data, config),
    delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.delete<T>(url, config),
    patch:  <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.patch<T>(url, data, config),
}

export default api
